import React, { useEffect, useMemo, useState } from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { type CrudFormProps } from '../../components/CrudPage';
import { MaterialControllerService, type MaterialDto, ResourceTypeControllerService, type ResourceTypeDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { useStore } from '../../../store';
import { isModuleLicensed } from '../../utils/license';
import { InfoIcon } from '../../components/InfoIcon';
import { resolveValidationMessage } from '../../utils/validationMessages';
import {
  activeGroups,
  createArticleSchema,
  isPieceArticle,
  isPredecessorCandidate,
  type ArticleFormData,
  type ArticleType,
} from './articleFormSchema';

type MaterialWithId = MaterialDto & { id: string };

// Units a stocked article that is not in pieces may have (pieces are derived, see isPieceArticle)
const STOCK_UNITS = [
  { value: 'KILOGRAM', text: 'Kilogramm (kg)' },
  { value: 'GRAM', text: 'Gramm (g)' },
  { value: 'LITER', text: 'Liter (l)' },
  { value: 'COUNT', text: 'Stück' },
];

const UNIT_TO_TYPE: Record<string, string> = {
  COUNT: 'PIECE',
  KILOGRAM: 'MASS',
  GRAM: 'MASS',
  LITER: 'VOLUME',
};

/** Lock icon of a property that an active routing locks; the tooltip names the routings. */
const LockIcon: React.FC<{ title: string }> = ({ title }) => (
  <span
    className="e-icons e-lock ml-2 text-gray-500 cursor-help"
    title={title}
    aria-label={title}
    data-testid="property-lock"
  />
);

/** Value of the origin dropdown for "made from dough/batter"; the other values are predecessor uuids. */
const ORIGIN_DOUGH = 'DOUGH';

/**
 * Heading of a field group. For an article property the heading is the checkbox of the property;
 * the fields below are enabled only while it is checked.
 */
const SectionHeading: React.FC<{
  title: string;
  info?: string;
  checkbox?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    /** Tooltip of the lock icon; set when an active routing locks the property. */
    lockedTitle?: string;
  };
}> = ({ title, info, checkbox }) => (
  <div className="mt-5 mb-2 flex items-center border-b border-gray-200 pb-1">
    {checkbox ? (
      <span className="article-section-checkbox font-semibold">
        <CheckBoxComponent
          label={title}
          checked={checkbox.checked}
          disabled={checkbox.disabled}
          change={(e: { checked?: boolean }) => checkbox.onChange(e.checked ?? false)}
        />
      </span>
    ) : (
      <span className="text-sm font-semibold text-gray-800">{title}</span>
    )}
    {info && <InfoIcon title={info} />}
    {checkbox?.lockedTitle && <LockIcon title={checkbox.lockedTitle} />}
  </div>
);

const ErrorText: React.FC<{ error?: unknown }> = ({ error }) => {
  const { t } = useTranslation(['common']);
  if (!error) return null;
  return <div className="text-red-500 text-sm mt-1">{resolveValidationMessage(t, error as never)}</div>;
};

/** A labelled numeric field bound to the form. */
const NumberField: React.FC<{
  control: Control<ArticleFormData>;
  errors: FieldErrors<ArticleFormData>;
  name: keyof ArticleFormData;
  label: string;
  required?: boolean;
  format?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  enabled?: boolean;
}> = ({ control, errors, name, label, required, format = 'n0', min, max, step, placeholder, enabled = true }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required ? ' *' : ''}</label>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <NumericTextBoxComponent
          value={(field.value as number | null | undefined) ?? undefined}
          change={(e: { value?: number | string | null }) => field.onChange(e.value ?? null)}
          placeholder={placeholder}
          floatLabelType="Never"
          format={format}
          min={min}
          max={max}
          step={step}
          enabled={enabled}
          htmlAttributes={{ 'aria-label': label }}
          cssClass={errors[name] ? 'e-error' : ''}
        />
      )}
    />
    <ErrorText error={errors[name]} />
  </div>
);

/**
 * One form for products and intermediate products ("Artikel"). "entsteht aus" is one dropdown
 * (dough/batter or a predecessor); every other property is the checkbox heading of its field group,
 * and the fields below it are shown only while it is checked
 * (docs/CONCEPT_Feingebaeck_Artikeleigenschaften.md, 5.1). Until phase 5 the page decides "stocked":
 * products are not stocked, intermediate products are. Values of collapsed groups are kept as they are.
 */
const ArticleFormDialog: React.FC<CrudFormProps<MaterialWithId> & { materialType: ArticleType }> = ({
  value,
  onClose,
  onSave,
  materialType,
}) => {
  const { t } = useTranslation(['common']);
  const [ovenTypes, setOvenTypes] = useState<ResourceTypeDto[]>([]);
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const isProduct = materialType === 'PRODUCT';
  const locked = value?.propertiesLocked === true;
  const userProfile = useStore((state) => state.userProfile);
  // Rough planning fields only matter where the planning area has the PLANNING module
  const showRoughPlanning = isProduct && isModuleLicensed(userProfile, 'PLANNING');

  useEffect(() => {
    let cancelled = false;
    ResourceTypeControllerService.findByCategory('OVEN')
      .then(types => { if (!cancelled) setOvenTypes(types); })
      .catch(error => { console.error('Error loading oven types:', error); });
    MaterialControllerService.findAll()
      .then(all => { if (!cancelled) setMaterials(all); })
      .catch(error => { console.error('Error loading materials:', error); });
    return () => { cancelled = true; };
  }, []);

  const ovenTypeByUuid = useMemo(
    () => Object.fromEntries(ovenTypes.map(type => [type.uuid ?? '', type.ovenType])),
    [ovenTypes],
  );
  const schema = useMemo(
    () => createArticleSchema(t as never, { materialType, ovenTypeByUuid }),
    [t, materialType, ovenTypeByUuid],
  );
  const predecessorCandidates = useMemo(
    () => materials.filter(m => isPredecessorCandidate(m) && m.uuid !== value?.uuid),
    [materials, value?.uuid],
  );
  const originOptions = useMemo(
    () => [
      { value: ORIGIN_DOUGH, text: t('article.originDough', { defaultValue: 'Teig/Masse' }) },
      ...predecessorCandidates.map(m => ({ value: m.uuid, text: m.name })),
    ],
    [predecessorCandidates, t],
  );
  const aliasProducts = useMemo(
    () => materials.filter(m => m.materialType === 'PRODUCT' && !m.isAlias && m.uuid !== value?.uuid),
    [materials, value?.uuid],
  );

  const origin = (value?.origin as 'DOUGH' | 'PIECES' | undefined) ?? (value?.predecessorUuid ? 'PIECES' : 'DOUGH');
  const initialValue: ArticleFormData = {
    name: value?.name ?? '',
    articleNumber: value?.articleNumber ?? '',
    description: value?.description ?? null,
    baseUnit: value?.baseUnit ?? (isProduct ? 'COUNT' : 'KILOGRAM'),
    global: value?.global ?? false,
    origin,
    predecessorUuid: value?.predecessorUuid ?? null,
    separated: value?.separated ?? (isProduct && origin === 'DOUGH'),
    baked: value?.baked ?? isProduct,
    longRunning: value?.longRunning ?? false,
    longTermOffsetDays: value?.longTermOffsetDays ?? null,
    isAlias: value?.isAlias ?? false,
    aliasForProductUuid: value?.aliasForProductUuid ?? null,
    minDoughQuantityInKg: value?.minDoughQuantityInKg ?? (isProduct ? null : 0),
    maxDoughQuantityInKg: value?.maxDoughQuantityInKg ?? (isProduct ? null : 250),
    doughBatchMethod: value?.doughBatchMethod ?? 'EVEN',
    initialWeighInGrams: value?.initialWeighInGrams ?? null,
    scrapRate: value?.scrapRate ?? 0,
    requireContiguousCampaignBatches: value?.requireContiguousCampaignBatches ?? false,
    defaultOvenTypeUuid: value?.defaultOvenTypeUuid ?? null,
    piecesPerOven: value?.piecesPerOven || null,
    startTemperature: value?.startTemperature ?? null,
    endTemperature: value?.endTemperature ?? null,
    ovenBatchDetermination: (() => {
      const raw = value?.ovenBatchDetermination ?? value?.batchDetermination ?? 'FULL_OVEN';
      return raw === 'MANUAL' ? 'OVEN_UTILIZATION' : raw;
    })(),
    minOvenUtilizationPercent: value?.minOvenUtilizationPercent ?? null,
    planningMode: (value?.planningMode as 'AUTO' | 'MANUAL' | undefined) ?? 'AUTO',
    minLotSize: value?.minLotSize ?? null,
    maxLotSize: value?.maxLotSize ?? null,
    lotSizeMultiple: value?.lotSizeMultiple ?? null,
    maxStorageDays: value?.maxStorageDays ?? null,
    maxProductionLeadTimeHours: value?.maxProductionLeadTimeHours ?? null,
    excludedFromRoughPlanning: value?.excludedFromRoughPlanning ?? false,
  };

  const ovenBatchDeterminationOptions = [
    { text: t('product.batchDeterminationValues.fullOven'), value: 'FULL_OVEN' },
    { text: t('product.batchDeterminationValues.fullStove'), value: 'FULL_STOVE' },
    { text: t('product.batchDeterminationValues.fullCauldron'), value: 'FULL_CAULDRON' },
    { text: t('product.batchDeterminationValues.ovenUtilization'), value: 'OVEN_UTILIZATION' },
  ];
  const doughBatchMethodOptions = [
    { text: t('product.doughBatchMethodValues.even'), value: 'EVEN' },
    { text: t('product.doughBatchMethodValues.full'), value: 'FULL' },
  ];

  return (
    <FormDialog<ArticleFormData>
      value={initialValue}
      onClose={onClose}
      onSave={(data) => {
        const pieces = isPieceArticle(data);
        const baseUnit = pieces ? 'COUNT' : (isProduct ? 'KILOGRAM' : (data.baseUnit || 'KILOGRAM'));
        const article: MaterialWithId = {
          // Keep the values of hidden groups and fields this form does not edit
          ...(value ?? {}),
          id: value?.id ?? '',
          uuid: value?.uuid,
          tenantId: value?.tenantId,
          tenantName: value?.tenantName,
          materialType,
          name: data.name,
          articleNumber: data.articleNumber || undefined,
          description: data.description || undefined,
          global: data.global ?? false,
          baseUnit,
          baseUnitType: UNIT_TO_TYPE[baseUnit] ?? 'PIECE',
          origin: data.origin,
          predecessorUuid: data.origin === 'PIECES' ? (data.predecessorUuid ?? undefined) : undefined,
          separated: data.origin === 'PIECES' ? false : data.separated,
          baked: data.baked,
          stocked: !isProduct,
          longRunning: data.longRunning,
          longTermOffsetDays: data.longRunning ? (data.longTermOffsetDays ?? undefined) : undefined,
          minDoughQuantityInKg: data.minDoughQuantityInKg ?? 0,
          maxDoughQuantityInKg: data.maxDoughQuantityInKg ?? 250,
          doughBatchMethod: data.doughBatchMethod ?? undefined,
          initialWeighInGrams: data.initialWeighInGrams ?? 0,
          // scrap_rate is NOT NULL in the database; without an explicit 0 the INSERT fails
          scrapRate: data.scrapRate ?? 0,
          defaultOvenTypeUuid: data.defaultOvenTypeUuid || undefined,
          piecesPerOven: data.piecesPerOven ?? 0,
          startTemperature: data.startTemperature ?? 0,
          endTemperature: data.endTemperature ?? 0,
          ovenBatchDetermination: data.ovenBatchDetermination ?? undefined,
          minOvenUtilizationPercent: data.ovenBatchDetermination === 'OVEN_UTILIZATION'
            ? (data.minOvenUtilizationPercent ?? undefined)
            : undefined,
          isAlias: isProduct ? (data.isAlias ?? false) : false,
          aliasForProductUuid: isProduct && data.isAlias ? (data.aliasForProductUuid ?? undefined) : undefined,
          requireContiguousCampaignBatches: data.requireContiguousCampaignBatches ?? false,
          maxProductionLeadTimeHours: isProduct ? (data.maxProductionLeadTimeHours ?? undefined) : undefined,
          planningMode: isProduct ? undefined : data.planningMode,
          minLotSize: data.minLotSize ?? undefined,
          maxLotSize: data.maxLotSize ?? undefined,
          lotSizeMultiple: data.lotSizeMultiple ?? undefined,
          minCoverageDays: value?.minCoverageDays ?? 1,
          leadTimeDays: value?.leadTimeDays ?? 0,
          maxStorageDays: !isProduct ? (data.maxStorageDays ?? undefined) : undefined,
          excludedFromRoughPlanning: isProduct ? (data.excludedFromRoughPlanning ?? false) : undefined,
        };
        onSave(article);
      }}
      titleKey={isProduct ? 'dialogs.material' : 'dialogs.intermediateProduct'}
      width="900px"
      resolver={zodResolver(schema) as never}
    >
      {({ control, formState: { errors }, watch, setValue }) => {
        const data = watch();
        const groups = activeGroups({ materialType, ...data });
        const isAlias = isProduct && data.isAlias === true;
        const pieces = isPieceArticle(data);
        const selectedOvenType = ovenTypeByUuid[data.defaultOvenTypeUuid ?? ''];
        const isFryer = selectedOvenType === 'FRYER';
        const availableOvenBatchDeterminations = selectedOvenType === 'STONE_OVEN'
          ? ovenBatchDeterminationOptions
          : ovenBatchDeterminationOptions.filter(option => option.value !== 'FULL_STOVE');
        const editable = !isAlias;
        const propertiesDisabled = locked || isAlias;
        const lockingRoutings = value?.propertiesLockedBy ?? [];
        let lockedTitle: string | undefined;
        if (locked) {
          lockedTitle = lockingRoutings.length > 0
            ? t('article.lockedBy', {
              routings: lockingRoutings.join(', '),
              defaultValue: 'Gesperrt durch Arbeitsplan: {{routings}}. Zum Ändern die Arbeitspläne auf inaktiv setzen.',
            })
            : t('article.locked', {
              defaultValue: 'Gesperrt durch einen aktiven Arbeitsplan. Zum Ändern die Arbeitspläne auf inaktiv setzen.',
            });
        }
        const fromPredecessor = data.origin === 'PIECES';

        return (
          <>
            {/* Basic data */}
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.name')} *</label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextBoxComponent
                      value={field.value ?? ''}
                      change={(e: { value?: string }) => field.onChange(e.value ?? '')}
                      placeholder={t('form.name') + ' *'}
                      floatLabelType="Never"
                      cssClass={errors.name ? 'e-error' : ''}
                    />
                  )}
                />
                <ErrorText error={errors.name} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('material.articleNumber')}{isProduct ? '' : ' *'}
                </label>
                <Controller
                  name="articleNumber"
                  control={control}
                  render={({ field }) => (
                    <TextBoxComponent
                      value={field.value ?? ''}
                      change={(e: { value?: string }) => field.onChange(e.value ?? '')}
                      placeholder={t('material.articleNumber')}
                      floatLabelType="Never"
                      cssClass={errors.articleNumber ? 'e-error' : ''}
                    />
                  )}
                />
                <ErrorText error={errors.articleNumber} />
              </div>
            </div>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.description')}</label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextBoxComponent
                      value={field.value ?? ''}
                      change={(e: { value?: string }) => field.onChange(e.value ?? '')}
                      placeholder={t('form.description')}
                      floatLabelType="Never"
                    />
                  )}
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  {t('form.baseUnit')}
                  <InfoIcon title={t('article.infoUnit', {
                    defaultValue: 'Artikel, die vereinzelt werden oder aus einer Vorstufe entstehen, werden in Stück geführt.',
                  })} />
                </label>
                {!isProduct && !pieces ? (
                  <Controller
                    name="baseUnit"
                    control={control}
                    render={({ field }) => (
                      <DropDownListComponent
                        dataSource={STOCK_UNITS}
                        fields={{ text: 'text', value: 'value' }}
                        floatLabelType="Never"
                        value={field.value ?? 'KILOGRAM'}
                        change={(e: { value?: string }) => field.onChange(e.value ?? 'KILOGRAM')}
                      />
                    )}
                  />
                ) : (
                  <div className="py-1 text-sm text-gray-700" data-testid="article-unit">
                    {pieces ? t('form.baseUnit.piece') : t('form.baseUnit.kilogram')}
                  </div>
                )}
              </div>
            </div>

            {isProduct && (
              <div className="mb-4 flex items-center gap-3 flex-nowrap min-w-0">
                <Controller
                  name="isAlias"
                  control={control}
                  render={({ field }) => (
                    <CheckBoxComponent
                      label={t('product.isAlias')}
                      checked={field.value ?? false}
                      change={(e: { checked?: boolean }) => {
                        field.onChange(e.checked ?? false);
                        if (!e.checked) setValue('aliasForProductUuid', null);
                      }}
                    />
                  )}
                />
                {isAlias && (
                  <div className="min-w-0 flex-1">
                    <Controller
                      name="aliasForProductUuid"
                      control={control}
                      render={({ field }) => (
                        <DropDownListComponent
                          dataSource={aliasProducts as never}
                          fields={{ text: 'name', value: 'uuid' }}
                          placeholder={t('product.aliasForProduct')}
                          floatLabelType="Never"
                          value={field.value ?? undefined}
                          locale="de-DE"
                          cssClass={errors.aliasForProductUuid ? 'e-error' : ''}
                          change={(e: { value?: string }) => {
                            field.onChange(e.value ?? null);
                            const source = aliasProducts.find(p => p.uuid === e.value);
                            if (source) {
                              setValue('origin', (source.origin as 'DOUGH' | 'PIECES') ?? 'DOUGH');
                              setValue('predecessorUuid', source.predecessorUuid ?? null);
                              setValue('separated', source.separated ?? true);
                              setValue('baked', source.baked ?? true);
                              setValue('longRunning', source.longRunning ?? false);
                              setValue('longTermOffsetDays', source.longTermOffsetDays ?? null);
                            }
                          }}
                        />
                      )}
                    />
                    <ErrorText error={errors.aliasForProductUuid} />
                  </div>
                )}
              </div>
            )}

            {/* Origin: dough/batter or a predecessor, one dropdown */}
            <div className="mt-5 mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  {t('article.origin', { defaultValue: 'entsteht aus' })} *
                  <InfoIcon title={t('article.infoOrigin', {
                    defaultValue: 'Teig/Masse: Der Artikel wird aus Teig oder Masse hergestellt. Eine Vorstufe ist ein bevorratetes Vorprodukt in Stück, aus dem der Artikel Stück für Stück entsteht, z. B. Puddingplunder aus Puddingplunder ROH; Teig/Masse und Vereinzelung entfallen dann.',
                  })} />
                  {lockedTitle && <LockIcon title={lockedTitle} />}
                </label>
                <DropDownListComponent
                  dataSource={originOptions as never}
                  fields={{ text: 'text', value: 'value' }}
                  placeholder={t('article.origin', { defaultValue: 'entsteht aus' })}
                  floatLabelType="Never"
                  value={fromPredecessor ? (data.predecessorUuid ?? undefined) : ORIGIN_DOUGH}
                  enabled={!propertiesDisabled}
                  locale="de-DE"
                  cssClass={errors.predecessorUuid ? 'e-error' : ''}
                  change={(e: { value?: string | null }) => {
                    if (!e.value || e.value === ORIGIN_DOUGH) {
                      setValue('origin', 'DOUGH');
                      setValue('predecessorUuid', null);
                    } else {
                      setValue('origin', 'PIECES');
                      setValue('predecessorUuid', e.value);
                      setValue('separated', false);
                    }
                  }}
                />
                <ErrorText error={errors.predecessorUuid} />
                {predecessorCandidates.length === 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {t('article.noPredecessorCandidates', {
                      defaultValue: 'Kein passender Artikel: Eine Vorstufe ist ein Vorprodukt, das vereinzelt ist oder selbst aus einer Vorstufe entsteht.',
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Dough/batter: only for articles made from dough/batter */}
            {groups.dough && (
              <>
            <SectionHeading title={t('article.groupDough', { defaultValue: 'Teig/Masse' })} />
            <div className="mb-4 grid grid-cols-2 gap-4">
              <NumberField control={control} errors={errors} name="minDoughQuantityInKg" label={t('material.minDoughQuantity')} required enabled={editable} />
              <NumberField control={control} errors={errors} name="maxDoughQuantityInKg" label={t('material.maxDoughQuantity')} required enabled={editable} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.doughBatchMethod')}</label>
                <Controller
                  name="doughBatchMethod"
                  control={control}
                  render={({ field }) => (
                    <DropDownListComponent
                      dataSource={doughBatchMethodOptions}
                      fields={{ text: 'text', value: 'value' }}
                      floatLabelType="Never"
                      value={field.value ?? undefined}
                      enabled={editable}
                      change={(e: { value?: string }) => field.onChange(e.value ?? null)}
                    />
                  )}
                />
              </div>
            </div>
              </>
            )}

            {/* Separation: not for articles made from a predecessor, they are already in pieces */}
            {!fromPredecessor && (
            <SectionHeading
              title={t('article.separated', { defaultValue: 'wird vereinzelt' })}
              info={t('article.infoSeparated', {
                defaultValue: 'Der Teig wird in Stücke geteilt. Im Arbeitsplan ist dafür ein Vorgang als Vereinzelung markiert, ab dort wird in Stück gerechnet.',
              })}
              checkbox={{
                checked: data.separated === true,
                disabled: propertiesDisabled,
                lockedTitle,
                onChange: (checked) => setValue('separated', checked),
              }}
            />
            )}
            {groups.separation && (
              <>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <NumberField control={control} errors={errors} name="initialWeighInGrams" label={t('material.initialWeighIn')} required enabled={editable} />
              <NumberField control={control} errors={errors} name="scrapRate" label={t('material.scrapRate')} format="p2" min={0} max={0.9999} step={0.0001} enabled={editable} />
            </div>
            {isProduct && (
              <div className="mb-4">
                <Controller
                  name="requireContiguousCampaignBatches"
                  control={control}
                  render={({ field }) => (
                    <CheckBoxComponent
                      label={t('product.requireContiguousCampaignBatches', 'Aufarbeitung: Alle Chargen einer Kampagne am Stück')}
                      checked={field.value ?? false}
                      disabled={!editable}
                      change={(e: { checked?: boolean }) => field.onChange(e.checked ?? false)}
                    />
                  )}
                />
              </div>
            )}
              </>
            )}

            {/* Oven */}
            <SectionHeading
              title={t('article.baked', { defaultValue: 'wird gebacken' })}
              info={t('article.infoBaked', {
                defaultValue: 'Der Artikel durchläuft einen Ofen oder eine Fritteuse; der Arbeitsplan braucht dann einen Ofenschritt. In einer Kette wird höchstens einmal gebacken.',
              })}
              checkbox={{
                checked: data.baked === true,
                disabled: propertiesDisabled,
                lockedTitle,
                onChange: (checked) => setValue('baked', checked),
              }}
            />
            {groups.oven && (
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.defaultOvenType')} *</label>
                <Controller
                  name="defaultOvenTypeUuid"
                  control={control}
                  render={({ field }) => (
                    <DropDownListComponent
                      dataSource={ovenTypes as never}
                      fields={{ text: 'name', value: 'uuid' }}
                      placeholder={t('product.defaultOvenType')}
                      floatLabelType="Never"
                      value={field.value ?? undefined}
                      enabled={editable}
                      locale="de-DE"
                      cssClass={errors.defaultOvenTypeUuid ? 'e-error' : ''}
                      change={(e: { value?: string | null }) => {
                        field.onChange(e.value || null);
                        const nextType = ovenTypeByUuid[e.value ?? ''];
                        if (nextType !== 'STONE_OVEN' && watch('ovenBatchDetermination') === 'FULL_STOVE') {
                          setValue('ovenBatchDetermination', 'FULL_OVEN');
                        }
                      }}
                    />
                  )}
                />
                <ErrorText error={errors.defaultOvenTypeUuid} />
              </div>
              <NumberField
                control={control}
                errors={errors}
                name="piecesPerOven"
                label={isFryer ? t('article.piecesPerBatch', { defaultValue: 'Stück pro Charge' }) : t('product.piecesPerOven')}
                required
                min={1}
                enabled={editable}
              />
              {!isFryer && (
                <>
                  <NumberField control={control} errors={errors} name="startTemperature" label={t('product.startTemperature')} required min={0} enabled={editable} />
                  <NumberField control={control} errors={errors} name="endTemperature" label={t('product.endTemperature')} required min={0} enabled={editable} />
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.ovenBatchDetermination')} *</label>
                <Controller
                  name="ovenBatchDetermination"
                  control={control}
                  render={({ field }) => (
                    <DropDownListComponent
                      dataSource={availableOvenBatchDeterminations}
                      fields={{ text: 'text', value: 'value' }}
                      floatLabelType="Never"
                      value={field.value ?? undefined}
                      enabled={editable}
                      cssClass={errors.ovenBatchDetermination ? 'e-error' : ''}
                      change={(e: { value?: string }) => field.onChange(e.value ?? null)}
                    />
                  )}
                />
                <ErrorText error={errors.ovenBatchDetermination} />
              </div>
              {data.ovenBatchDetermination === 'OVEN_UTILIZATION' && (
                <NumberField control={control} errors={errors} name="minOvenUtilizationPercent" label={t('product.minOvenUtilizationPercent')} required format="n1" step={0.1} min={0} enabled={editable} />
              )}
            </div>
            )}

            {/* Long-running */}
            <SectionHeading
              title={t('article.longRunning', { defaultValue: 'wird langzeitgeführt' })}
              info={t('article.infoLongRunning', {
                defaultValue: 'Die Herstellung verteilt sich auf zwei Tage: Der Teig wird vorher hergestellt, das Ausbacken liegt um den Versatz in Tagen später.',
              })}
              checkbox={{
                checked: data.longRunning === true,
                disabled: propertiesDisabled,
                lockedTitle,
                onChange: (checked) => setValue('longRunning', checked),
              }}
            />
            {groups.longTerm && (
            <div className="mb-4 grid grid-cols-2 gap-4">
              <NumberField control={control} errors={errors} name="longTermOffsetDays" label={t('product.longTermOffsetDays')} required min={1} max={31} placeholder="1–31" enabled={editable} />
            </div>
            )}

            {/* Stock (intermediate products) or freshness (products); the page decides until phase 5 */}
            {groups.stock && (
              <>
                <SectionHeading
                  title={t('article.groupStock', { defaultValue: 'Lager' })}
                  info={t('article.infoStock', {
                    defaultValue: 'Vorprodukte werden bevorratet: Sie werden auf Lager produziert und nach Losgröße geplant. Nicht bevorratete Artikel werden unter Produkte gepflegt.',
                  })}
                />
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <NumberField control={control} errors={errors} name="minLotSize" label={t('intermediateProduct.minLotSize')} format="n3" min={0} />
                  <NumberField control={control} errors={errors} name="maxLotSize" label={t('intermediateProduct.maxLotSize')} format="n3" min={0} />
                  <NumberField control={control} errors={errors} name="lotSizeMultiple" label={t('intermediateProduct.lotSizeMultiple')} format="n3" min={0} />
                  <NumberField
                    control={control}
                    errors={errors}
                    name="maxStorageDays"
                    label={t('article.maxStorageDays', { defaultValue: 'Lagerdauer (Tage)' })}
                    min={1}
                    placeholder={t('article.maxStorageDaysPlaceholder', { defaultValue: 'leer = ohne Grenze' })}
                  />
                </div>
              </>
            )}
            {groups.freshness && (
              <>
                <SectionHeading
                  title={t('article.groupFreshness', { defaultValue: 'Frische' })}
                  info={t('article.infoFreshness', {
                    defaultValue: 'Produkte werden nicht bevorratet, sondern frisch nach Backzettel produziert. Bevorratete Artikel werden unter Vorprodukte gepflegt.',
                  })}
                />
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" title={t('product.maxProductionLeadTimeDaysHelp')}>
                      {t('product.maxProductionLeadTimeDays')}
                    </label>
                    <Controller
                      name="maxProductionLeadTimeHours"
                      control={control}
                      render={({ field }) => (
                        <NumericTextBoxComponent
                          value={field.value != null ? field.value / 24 : undefined}
                          change={(e: { value?: number | string | null }) =>
                            field.onChange(e.value != null ? Math.round(Number(e.value) * 24) : null)}
                          placeholder={t('product.maxProductionLeadTimeDaysPlaceholder')}
                          floatLabelType="Never"
                          format="n0"
                          min={0}
                          enabled={editable}
                          cssClass={errors.maxProductionLeadTimeHours ? 'e-error' : ''}
                        />
                      )}
                    />
                    <ErrorText error={errors.maxProductionLeadTimeHours} />
                  </div>
                </div>
              </>
            )}

            {/* Rough planning */}
            {showRoughPlanning && (
              <>
                <SectionHeading title={t('article.groupRoughPlanning', { defaultValue: 'Grobplanung' })} />
                <div className="mb-4">
                  <Controller
                    name="excludedFromRoughPlanning"
                    control={control}
                    render={({ field }) => (
                      <CheckBoxComponent
                        label={t('product.excludedFromRoughPlanning', { defaultValue: 'Von der Grobplanung ausnehmen' })}
                        checked={field.value ?? false}
                        change={(e: { checked?: boolean }) => field.onChange(e.checked ?? false)}
                      />
                    )}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('product.excludedFromRoughPlanningHelp', { defaultValue: 'Das Produkt fehlt in der Planungstafel und im Optimierungslauf der Grobplanung.' })}
                  </p>
                </div>
              </>
            )}

            <div className="mt-4 mb-2">
              <Controller
                name="global"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    label={t('form.global')}
                    checked={field.value ?? false}
                    change={(e: { checked?: boolean }) => field.onChange(e.checked ?? false)}
                  />
                )}
              />
            </div>
          </>
        );
      }}
    </FormDialog>
  );
};

export default ArticleFormDialog;
